import { Link } from 'react-router-dom'

import {
  CallInitialsLandmark,
  callerPrimaryLabel,
  callRowSubtitle,
} from '@/components/calls/call-identity-landmark'
import { Badge } from '@/components/ui/badge'
import type { CallRecordListItem } from '@/hooks/use-call-records'
import { formatRelativeTimeIso } from '@/lib/time-format'
import { cn } from '@/lib/utils'

export type CallQueueRowVariant = 'live' | 'evaluation'

function badgeVariantForStatus(status: string) {
  if (status === 'ended' || status === 'completed') return 'secondary' as const
  if (status === 'in-progress' || status === 'ringing' || status === 'evaluating')
    return 'default' as const
  if (status === 'evaluation_failed') return 'destructive' as const
  return 'outline' as const
}

function badgeVariantForEval(status: string) {
  if (status === 'completed') return 'secondary' as const
  if (status === 'evaluating') return 'default' as const
  if (status === 'evaluation_failed') return 'destructive' as const
  return 'outline' as const
}

type CallQueueRowBase = {
  className?: string
  showEvalSummary?: boolean
}

export type CallQueueRowProps = CallQueueRowBase & {
  variant: CallQueueRowVariant
  row: CallRecordListItem
}

export function CallQueueRow(props: CallQueueRowProps) {
  const { variant, className, showEvalSummary = false } = props
  const { row } = props
  const callerLabel = callerPrimaryLabel(row)
  const subtitle = callRowSubtitle(row)
  const callState = (row.call_state || 'routine').toLowerCase()
  const evalSummary = row.evaluation_summary?.trim() ?? ''
  const triageSummary = row.triage_summary?.trim() ?? ''
  const preview =
    variant === 'evaluation'
      ? showEvalSummary && evalSummary
        ? evalSummary
        : triageSummary || evalSummary || row.summary_preview?.trim() || null
      : triageSummary || row.live_transcript_preview?.trim() || row.summary_preview?.trim() || null

  return (
    <Link
      to={`/calls/records/${row.id}`}
      className={cn(
        'flex items-start gap-3 rounded-lg border bg-card p-3 text-sm transition-colors hover:bg-muted/50',
        callState === 'emergency' && 'border-destructive/60 ring-1 ring-destructive/20',
        callState === 'elevated' && 'border-warning/60 ring-1 ring-warning/20',
        className
      )}
    >
      <CallInitialsLandmark row={row} size="row" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold leading-tight truncate">{callerLabel}</div>
            {subtitle ? (
              <div className="text-xs text-muted-foreground truncate mt-0.5">
                {subtitle}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5 shrink-0">
            {!row.is_complete ? (
              <Badge variant="default" className="font-normal">
                Live
              </Badge>
            ) : null}
            {callState === 'emergency' ? (
              <Badge variant="destructive" className="font-normal">
                Emergency
              </Badge>
            ) : null}
            {callState === 'elevated' ? (
              <Badge variant="warningSoft" className="font-normal">
                Elevated
              </Badge>
            ) : null}
            {variant === 'evaluation' ? (
              <Badge
                variant={badgeVariantForEval(row.evaluation_status)}
                className="font-normal"
              >
                {row.evaluation_status}
              </Badge>
            ) : (
              <Badge
                variant={badgeVariantForStatus(row.call_status)}
                className="font-normal"
              >
                {row.call_status || 'unknown'}
              </Badge>
            )}
          </div>
        </div>
        {preview ? (
          <p className="text-xs text-muted-foreground line-clamp-2">{preview}</p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Updated {formatRelativeTimeIso(row.updated_at)}
        </p>
      </div>
    </Link>
  )
}
